// ** React Imports
import { createContext, useEffect, useState, ReactNode } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** Axios
import axiosInstance from '../services/serverAxios'

// ** Config
import authConfig from 'src/configs/auth'

import toast from 'react-hot-toast'

// ** Types
import {
  AuthValuesType,
  RegisterParams,
  LoginParams,
  ErrCallbackType,
  ForgotPasswordParams,
  ChangePasswordParams,
  ChildTenant
} from '../types/user.type'
import googleAuthenticatorService from 'src/services/googleAuthenticator.service'
import tenantService from 'src/services/tenant.service'
import { User } from 'src/models/user'

// ** Defaults
const defaultProvider: AuthValuesType = {
  user: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  isInitialized: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setIsInitialized: () => Boolean,
  register: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  googleAuthenticator: () => Promise.resolve(),
  comparisonBy: 'previousMonth',
  setComparisonBy: () => String,
  selectedChildTenant: null,
  availableChildTenants: [],
  switchTenant: () => Promise.resolve(),
  clearTenantSelection: () => Promise.resolve()
}

const AuthContext = createContext(defaultProvider)

type Props = {
  children: ReactNode
}

const AuthProvider = ({ children }: Props) => {
  // ** States
  const [user, setUser] = useState<User | null>(defaultProvider.user)
  const [loading, setLoading] = useState<boolean>(defaultProvider.loading)
  const [isInitialized, setIsInitialized] = useState<boolean>(defaultProvider.isInitialized)
  const [comparisonBy, setComparisonBy] = useState<string>(defaultProvider.comparisonBy)
  const [selectedChildTenant, setSelectedChildTenant] = useState<ChildTenant | null>(null)
  const [availableChildTenants, setAvailableChildTenants] = useState<ChildTenant[]>([])

  // ** Hooks
  const router = useRouter()

  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      setIsInitialized(true)
      const storedToken = window.localStorage.getItem(authConfig.storageTokenKeyName)!
      if (storedToken) {
        setLoading(true)
        await axiosInstance
          .get(authConfig.meEndpoint)
          .then(async response => {
            const userData = new User({ ...response.data.data })
            setUser(userData)

            // Set master tenant data if available
            if (userData.isMasterTenant) {
              setAvailableChildTenants(userData.availableChildTenants || [])
              setSelectedChildTenant(userData.selectedChildTenant || null)
            }

            window.localStorage.setItem(authConfig.storageTokenKeyName, response.data.data.email)
            window.localStorage.setItem('userData', JSON.stringify(response.data.data))
            setLoading(false)
          })
          .catch(() => {
            localStorage.removeItem('userData')
            localStorage.removeItem(authConfig.storageTokenKeyName)
            setUser(null)
            setLoading(false)
          })
      } else {
        setLoading(false)

        /*
         * Task: when Dashbi times out, it "spins" on any device without letting you know you need to log back in when it times out. I want it to go back to a login screen.
         */

        // If the token is not found, remove the user data from the local storage
        localStorage.removeItem('userData')
        localStorage.removeItem(authConfig.storageTokenKeyName)

        // Set the user to null to indicate that the user is not logged in
        setUser(null)

        // Set the isInitialized to false to indicate that the initialization is done
        setIsInitialized(false)
      }
    }
    initAuth()
  }, [])

  const handleLogin = (params: LoginParams, errorCallback?: ErrCallbackType) => {
    axiosInstance
      .post(authConfig.loginEndpoint, params)
      .then(async res => {
        const userData = new User({ ...res.data.data })
        setUser(userData)
        
        // Set master tenant data if available
        if (userData.isMasterTenant) {
          setAvailableChildTenants(userData.availableChildTenants || [])
          setSelectedChildTenant(userData.selectedChildTenant || null)
        }
        
        window.localStorage.setItem(authConfig.storageTokenKeyName, res.data.data.email)
        window.localStorage.setItem('userData', JSON.stringify(res.data.data))
        if (res.data.data.googleAuthenticatorRequired === true) {
          // User must setup google authenticator
          if (res.data.data.googleAuthenticator === false) {
            toast.error(
              'Google Authenticator is required for two-factor authentication to ensure the utmost security while using our app. Please set it up to proceed.'
            )
            router.replace('/profile/')
          } else {
            if (router.asPath !== '/') {
              router.replace({
                pathname: '/login/google-authenticator/',
                query: { returnUrl: router.asPath }
              })
            } else {
              router.replace('/login/google-authenticator/')
            }
          }
        } else {
          const returnUrl = router.query.returnUrl
          const redirectURL = returnUrl && returnUrl !== '/' ? returnUrl : '/'
          router.replace(redirectURL as string)
        }
      })
      .catch(err => {
        if (errorCallback) errorCallback(err)
      })
  }

  const handleGoogleAuthenticator = (otpCode: string, errorCallback?: ErrCallbackType) => {
    googleAuthenticatorService
      .verify({ otp: otpCode, googleAuthenticatorSecret: null })
      .then(async res => {
        const userData = new User({ ...res.data.data })
        setUser(userData)
        
        // Set master tenant data if available
        if (userData.isMasterTenant) {
          setAvailableChildTenants(userData.availableChildTenants || [])
          setSelectedChildTenant(userData.selectedChildTenant || null)
        }
        
        window.localStorage.setItem(authConfig.storageTokenKeyName, res.data.data.email)
        window.localStorage.setItem('userData', JSON.stringify(res.data.data))
        const returnUrl = router.query.returnUrl
        const redirectURL = returnUrl && returnUrl !== '/' ? returnUrl : '/'
        router.replace(redirectURL as string)
      })
      .catch(err => {
        if (errorCallback) errorCallback(err)
      })
  }

  const handleForgotPassword = (params: ForgotPasswordParams, errorCallback?: ErrCallbackType) => {
    axiosInstance
      .post(authConfig.forgotPasswordEndpoint, params)
      .then(async () => {
        toast.success('We have sent your password reset link to your email')
        router.push('/login')
      })
      .catch(err => {
        if (errorCallback) errorCallback(err)
      })
  }

  const handleChangePassword = (params: ChangePasswordParams, errorCallback?: ErrCallbackType) => {
    axiosInstance
      .put(authConfig.resetPasswordEndpoint, params)
      .then(async () => {
        toast.success('Your password has been changed successfully')
        router.push('/login')
      })
      .catch(err => {
        if (errorCallback) errorCallback(err)
      })
  }

  const handleLogout = () => {
    setUser(null)
    setIsInitialized(false)
    setSelectedChildTenant(null)
    setAvailableChildTenants([])
    window.localStorage.removeItem('userData')
    window.localStorage.removeItem(authConfig.storageTokenKeyName)

    // Logout in the server for cookie destruction (including tenant cookies)
    axiosInstance.get(authConfig.logoutEndpoint).catch(err => {
      console.error('[ERROR] Logging out', err)
    })
    router.push('/login')
  }

  const handleRegister = (params: RegisterParams, errorCallback?: ErrCallbackType) => {
    axiosInstance
      .post(authConfig.registerEndpoint, params)
      .then(res => {
        if (res.data.error) {
          if (errorCallback) errorCallback(res.data.error)
        } else {
          handleLogin({ email: params.email, password: params.password })
        }
      })
      .catch((err: { [key: string]: string }) => (errorCallback ? errorCallback(err) : null))
  }

  // ** Tenant Switching Handlers
  const handleSwitchTenant = async (schemaName: string) => {
    try {
      const response = await tenantService.switchTenant(schemaName)
      setSelectedChildTenant(response.data)
      toast.success(response.message)
    } catch (error) {
      console.error('Failed to switch tenant:', error)
      toast.error('Failed to switch store')
      throw error
    }
  }

  const handleClearTenantSelection = async () => {
    try {
      await tenantService.clearSelection()
      setSelectedChildTenant(null)
      toast.success('Store selection cleared')
    } catch (error) {
      console.error('Failed to clear selection:', error)
      toast.error('Failed to clear store selection')
      throw error
    }
  }

  const values = {
    user,
    loading,
    setUser,
    setLoading,
    isInitialized,
    setIsInitialized,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister,
    forgotPassword: handleForgotPassword,
    changePassword: handleChangePassword,
    googleAuthenticator: handleGoogleAuthenticator,
    comparisonBy,
    setComparisonBy,
    selectedChildTenant,
    availableChildTenants,
    switchTenant: handleSwitchTenant,
    clearTenantSelection: handleClearTenantSelection
  }

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
